import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Download,
  People,
  ShoppingCart,
  TrendingUp,
  Map as MapIcon,
  TrackChanges,
} from '@mui/icons-material';
import api from '../services/api';

interface Driver {
  id: number;
  name: string;
}

const Reports: React.FC = () => {
  // Customer Report State
  const [customerStartDate, setCustomerStartDate] = useState('');
  const [customerEndDate, setCustomerEndDate] = useState('');
  const [customerPostcode, setCustomerPostcode] = useState('');
  const [customerLoading, setCustomerLoading] = useState(false);

  // Orders Report State
  const [ordersStartDate, setOrdersStartDate] = useState('');
  const [ordersEndDate, setOrdersEndDate] = useState('');
  const [ordersPostcode, setOrdersPostcode] = useState('');
  const [ordersLoading, setOrdersLoading] = useState(false);

  // Revenue Report State
  const [revenueStartDate, setRevenueStartDate] = useState('');
  const [revenueEndDate, setRevenueEndDate] = useState('');
  const [revenueGroupBy, setRevenueGroupBy] = useState('day');
  const [revenueLoading, setRevenueLoading] = useState(false);

  // Orders by Area Report State
  const [areaStartDate, setAreaStartDate] = useState('');
  const [areaEndDate, setAreaEndDate] = useState('');
  const [areaPostcode, setAreaPostcode] = useState('');
  const [areaLoading, setAreaLoading] = useState(false);

  // Order Sources Report State
  const [sourcesStartDate, setSourcesStartDate] = useState('');
  const [sourcesEndDate, setSourcesEndDate] = useState('');
  const [sourcesLoading, setSourcesLoading] = useState(false);

  // Record count previews
  const [customerCount, setCustomerCount] = useState<number | null>(null);
  const [ordersCount, setOrdersCount] = useState<number | null>(null);
  const [areaCount, setAreaCount] = useState<number | null>(null);
  const [sourcesCount, setSourcesCount] = useState<number | null>(null);

  // Shared filter data
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [serviceTypes, setServiceTypes] = useState<string[]>([]);

  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        const [driversRes, serviceTypesRes] = await Promise.all([
          api.get('/reports/drivers'),
          api.get('/reports/service-types'),
        ]);
        setDrivers(driversRes.data);
        setServiceTypes(serviceTypesRes.data);
      } catch (error) {
        console.error('Error fetching filter data:', error);
      }
    };
    fetchFilterData();
  }, []);

  // Fetch record count for a report
  const fetchRecordCount = async (
    reportType: string,
    params: Record<string, string>,
    setCount: (count: number | null) => void
  ) => {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });

      const response = await api.get(`/reports/record-count/${reportType}?${queryParams.toString()}`);
      setCount(response.data.count);
    } catch (error) {
      console.error(`Error fetching record count for ${reportType}:`, error);
      setCount(null);
    }
  };

  // Auto-fetch counts when filters change
  useEffect(() => {
    fetchRecordCount('customers', {
      start_date: customerStartDate,
      end_date: customerEndDate,
      postcode: customerPostcode,
    }, setCustomerCount);
  }, [customerStartDate, customerEndDate, customerPostcode]);

  useEffect(() => {
    fetchRecordCount('orders', {
      start_date: ordersStartDate,
      end_date: ordersEndDate,
      postcode: ordersPostcode,
    }, setOrdersCount);
  }, [ordersStartDate, ordersEndDate, ordersPostcode]);

  useEffect(() => {
    fetchRecordCount('orders-by-area', {
      start_date: areaStartDate,
      end_date: areaEndDate,
      postcode: areaPostcode,
    }, setAreaCount);
  }, [areaStartDate, areaEndDate, areaPostcode]);

  useEffect(() => {
    fetchRecordCount('order-sources', {
      start_date: sourcesStartDate,
      end_date: sourcesEndDate,
    }, setSourcesCount);
  }, [sourcesStartDate, sourcesEndDate]);

  const downloadReport = async (
    reportType: string,
    params: Record<string, string>,
    setLoading: (loading: boolean) => void
  ) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });

      const response = await api.get(`/reports/${reportType}?${queryParams.toString()}`, {
        responseType: 'blob',
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportType}_report_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(`Error downloading ${reportType} report:`, error);
      alert('Failed to download report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerDownload = () => {
    downloadReport('customers', {
      start_date: customerStartDate,
      end_date: customerEndDate,
      postcode: customerPostcode,
    }, setCustomerLoading);
  };

  const handleOrdersDownload = () => {
    downloadReport('orders', {
      start_date: ordersStartDate,
      end_date: ordersEndDate,
      postcode: ordersPostcode,
    }, setOrdersLoading);
  };

  const handleRevenueDownload = () => {
    downloadReport('revenue', {
      start_date: revenueStartDate,
      end_date: revenueEndDate,
      group_by: revenueGroupBy,
    }, setRevenueLoading);
  };

  const handleAreaDownload = () => {
    downloadReport('orders-by-area', {
      start_date: areaStartDate,
      end_date: areaEndDate,
      postcode: areaPostcode,
    }, setAreaLoading);
  };

  const handleSourcesDownload = () => {
    downloadReport('order-sources', {
      start_date: sourcesStartDate,
      end_date: sourcesEndDate,
    }, setSourcesLoading);
  };

  const formatStatus = (status: string) => {
    return status.replace('wc-', '').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Reports
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Generate and download Excel reports for customers, orders, revenue, area analysis, and driver performance.
      </Typography>

      <Grid container spacing={3}>
        {/* Customer Report Card */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <People sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Typography variant="h6">Customer Report</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Export customer list with contact details, registration dates, order counts, and total spend.
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Filters (optional)</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="From Date"
                    type="date"
                    size="small"
                    fullWidth
                    value={customerStartDate}
                    onChange={(e) => setCustomerStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="To Date"
                    type="date"
                    size="small"
                    fullWidth
                    value={customerEndDate}
                    onChange={(e) => setCustomerEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Postcode Area"
                    size="small"
                    fullWidth
                    value={customerPostcode}
                    onChange={(e) => setCustomerPostcode(e.target.value)}
                    placeholder="e.g. M1, M4, BL1 (comma-separated, exact match)"
                  />
                </Grid>
              </Grid>
              {customerCount !== null && (
                <Box sx={{ mt: 2, p: 1, bgcolor: 'success.light', borderRadius: 1 }}>
                  <Typography variant="body2" color="success.contrastText" fontWeight="bold">
                    {customerCount} customer{customerCount !== 1 ? 's' : ''} match your filters
                  </Typography>
                </Box>
              )}
            </CardContent>
            <CardActions sx={{ p: 2, pt: 0 }}>
              <Button
                variant="contained"
                fullWidth
                startIcon={customerLoading ? <CircularProgress size={20} color="inherit" /> : <Download />}
                onClick={handleCustomerDownload}
                disabled={customerLoading}
              >
                {customerLoading ? 'Generating...' : 'Download Excel'}
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Orders Report Card */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ShoppingCart sx={{ fontSize: 40, color: 'secondary.main', mr: 2 }} />
                <Typography variant="h6">Orders Report</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Export orders with customer details, addresses, totals, payment methods, and notes.
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Filters (optional)</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="From Date"
                    type="date"
                    size="small"
                    fullWidth
                    value={ordersStartDate}
                    onChange={(e) => setOrdersStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="To Date"
                    type="date"
                    size="small"
                    fullWidth
                    value={ordersEndDate}
                    onChange={(e) => setOrdersEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Postcode Area"
                    size="small"
                    fullWidth
                    value={ordersPostcode}
                    onChange={(e) => setOrdersPostcode(e.target.value)}
                    placeholder="e.g. M1, M4, BL1 (comma-separated, exact match)"
                  />
                </Grid>
              </Grid>
              {ordersCount !== null && (
                <Box sx={{ mt: 2, p: 1, bgcolor: 'success.light', borderRadius: 1 }}>
                  <Typography variant="body2" color="success.contrastText" fontWeight="bold">
                    {ordersCount} order{ordersCount !== 1 ? 's' : ''} match your filters
                  </Typography>
                </Box>
              )}
            </CardContent>
            <CardActions sx={{ p: 2, pt: 0 }}>
              <Button
                variant="contained"
                color="secondary"
                fullWidth
                startIcon={ordersLoading ? <CircularProgress size={20} color="inherit" /> : <Download />}
                onClick={handleOrdersDownload}
                disabled={ordersLoading}
              >
                {ordersLoading ? 'Generating...' : 'Download Excel'}
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Revenue Report Card */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUp sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                <Typography variant="h6">Revenue Report</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Export revenue summary with order counts, gross/net revenue, and tax.
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Filters (optional)</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="From Date"
                    type="date"
                    size="small"
                    fullWidth
                    value={revenueStartDate}
                    onChange={(e) => setRevenueStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="To Date"
                    type="date"
                    size="small"
                    fullWidth
                    value={revenueEndDate}
                    onChange={(e) => setRevenueEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Group By</InputLabel>
                    <Select
                      value={revenueGroupBy}
                      label="Group By"
                      onChange={(e) => setRevenueGroupBy(e.target.value)}
                    >
                      <MenuItem value="day">Daily</MenuItem>
                      <MenuItem value="week">Weekly</MenuItem>
                      <MenuItem value="month">Monthly</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
            <CardActions sx={{ p: 2, pt: 0 }}>
              <Button
                variant="contained"
                color="success"
                fullWidth
                startIcon={revenueLoading ? <CircularProgress size={20} color="inherit" /> : <Download />}
                onClick={handleRevenueDownload}
                disabled={revenueLoading}
              >
                {revenueLoading ? 'Generating...' : 'Download Excel'}
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Orders by Area Report Card */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <MapIcon sx={{ fontSize: 40, color: 'info.main', mr: 2 }} />
                <Typography variant="h6">Orders by Area</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Orders grouped by postcode prefix with count, revenue, and unique customers per area.
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Filters (optional)</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="From Date"
                    type="date"
                    size="small"
                    fullWidth
                    value={areaStartDate}
                    onChange={(e) => setAreaStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="To Date"
                    type="date"
                    size="small"
                    fullWidth
                    value={areaEndDate}
                    onChange={(e) => setAreaEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Postcode Area"
                    size="small"
                    fullWidth
                    value={areaPostcode}
                    onChange={(e) => setAreaPostcode(e.target.value)}
                    placeholder="e.g. M1, M4, BL1 (comma-separated, exact match)"
                  />
                </Grid>
              </Grid>
              {areaCount !== null && (
                <Box sx={{ mt: 2, p: 1, bgcolor: 'success.light', borderRadius: 1 }}>
                  <Typography variant="body2" color="success.contrastText" fontWeight="bold">
                    {areaCount} area{areaCount !== 1 ? 's' : ''} match your filters
                  </Typography>
                </Box>
              )}
            </CardContent>
            <CardActions sx={{ p: 2, pt: 0 }}>
              <Button
                variant="contained"
                sx={{ backgroundColor: '#0288d1', '&:hover': { backgroundColor: '#01579b' } }}
                fullWidth
                startIcon={areaLoading ? <CircularProgress size={20} color="inherit" /> : <Download />}
                onClick={handleAreaDownload}
                disabled={areaLoading}
              >
                {areaLoading ? 'Generating...' : 'Download Excel'}
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Order Sources Report Card */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrackChanges sx={{ fontSize: 40, color: 'error.main', mr: 2 }} />
                <Typography variant="h6">Order Sources</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Where customers found you: Google, Radio, Leaflets, etc. Includes automated tracking and customer responses.
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Filters (optional)</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="From Date"
                    type="date"
                    size="small"
                    fullWidth
                    value={sourcesStartDate}
                    onChange={(e) => setSourcesStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="To Date"
                    type="date"
                    size="small"
                    fullWidth
                    value={sourcesEndDate}
                    onChange={(e) => setSourcesEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
              {sourcesCount !== null && (
                <Box sx={{ mt: 2, p: 1, bgcolor: 'success.light', borderRadius: 1 }}>
                  <Typography variant="body2" color="success.contrastText" fontWeight="bold">
                    {sourcesCount} order{sourcesCount !== 1 ? 's' : ''} match your filters
                  </Typography>
                </Box>
              )}
              <Box sx={{ mt: 2, p: 1, bgcolor: 'info.light', borderRadius: 1 }}>
                <Typography variant="caption" color="info.contrastText">
                  Exports 2 sheets: Automated Tracking (Google, Bing, etc.) and Customer Responses (how they heard about you)
                </Typography>
              </Box>
            </CardContent>
            <CardActions sx={{ p: 2, pt: 0 }}>
              <Button
                variant="contained"
                color="error"
                fullWidth
                startIcon={sourcesLoading ? <CircularProgress size={20} color="inherit" /> : <Download />}
                onClick={handleSourcesDownload}
                disabled={sourcesLoading}
              >
                {sourcesLoading ? 'Generating...' : 'Download Excel'}
              </Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Reports;
